// Concernings structure with categories for tree hierarchy
export const concernings: { [key: string]: { label: string; category: string } } = {
    // Project level
    'project': { label: 'Project', category: 'Project' },

    // Review 1 - Customer Fit
    'review_1': { label: 'Review 1 - Customer Fit', category: 'Review 1' },
    'clients': { label: 'Clients', category: 'Review 1' },
    'companies': { label: 'Company and Investors', category: 'Review 1' },
    'solution': { label: 'Solution', category: 'Review 1' },

    // Review 2 - Solution
    'review_2': { label: 'Review 2 - Solution', category: 'Review 2' },
    'definition_of_success': { label: 'Definition of Success', category: 'Review 2' },
    'definition_of_failure': { label: 'Definition of Failure', category: 'Review 2' },
    'activity_chain': { label: 'Activity Chain', category: 'Review 2' },
    'economic_model': { label: 'Generation of Resources', category: 'Review 2' },
    'skills': { label: 'Skills', category: 'Review 2' },
    'all_benefits': { label: 'All Benefits', category: 'Review 2' },

    // Review 3 - Influencers
    'review_3': { label: 'Review 3 - Stakeholders', category: 'Review 3' },
    'factors': { label: 'Factors', category: 'Review 3' },
    'stakeholders': { label: 'Stakeholders', category: 'Review 3' },
    'collateral_damages': { label: 'Collateral Damages', category: 'Review 3' },

    // Review 4 - Actions and Investment
    'review_4': { label: 'Review 4 - Intellectual Properties', category: 'Review 4' },
    'moves': { label: 'Actions or Moves', category: 'Review 4' },
    'investment': { label: 'Investment', category: 'Review 4' },
    'intellectual_property': { label: 'Intellectual Property', category: 'Review 4' },

    // Client specific concernings
    'P / N / D': { label: 'Pain / Need / Desire', category: 'Client Details' },
    'Benchmarking': { label: 'Benchmarking', category: 'Client Details' },
    'Experience': { label: 'Experience', category: 'Client Details' },

    // Company specific concernings
    'Benefits': { label: 'Benefits', category: 'Company Details' },
    'Alignement': { label: 'Alignment', category: 'Company Details' },

    // Additional concernings from the original structure
    'pain': { label: 'Pain', category: 'Client Details' },
    'need': { label: 'Need', category: 'Client Details' },
    'desire': { label: 'Desire', category: 'Client Details' },

    // Review 1 specific items
    'review_1_clients': { label: 'Review 1 - Clients', category: 'Review 1' },
    'review_1_companies': { label: 'Review 1 - Companies', category: 'Review 1' },

    // Review 2 specific items
    'review_2_definition_of_success': { label: 'Review 2 - Definition of Success', category: 'Review 2' },
    'review_2_definition_of_failure': { label: 'Review 2 - Definition of Failure', category: 'Review 2' },
    'review_2_activity_chain': { label: 'Review 2 - Activity Chain', category: 'Review 2' },
    'review_2_economic_model': { label: 'Review 2 - Economic Model', category: 'Review 2' },

    // Review 3 specific items
    'review_3_factors': { label: 'Review 3 - Factors', category: 'Review 3' },
    'review_3_stakeholders': { label: 'Review 3 - Stakeholders', category: 'Review 3' },

    // Review 4 specific items
    'review_4_moves': { label: 'Review 4 - Moves', category: 'Review 4' },

    // Client-specific combinations
    'client_pain_need_desire': { label: 'Client - Pain/Need/Desire', category: 'Client Details' },
    'client_benchmarking': { label: 'Client - Benchmarking', category: 'Client Details' },
    'client_experience': { label: 'Client - Experience', category: 'Client Details' },

    // Company-specific combinations
    'company_benefits': { label: 'Company - Benefits', category: 'Company Details' },
    'company_alignment': { label: 'Company - Alignment', category: 'Company Details' },
    'company_benchmarking': { label: 'Company - Benchmarking', category: 'Company Details' },
};

// Extracts the string ref from a concerning (which may be a string like
// "clients_5" or an object { value, id }).
const concerningRef = (concerning: any): string => {
    if (typeof concerning === 'object' && concerning !== null) {
        return String(concerning.value ?? concerning.id ?? '');
    }
    return String(concerning);
};

// Drops concernings that point to a client or company that no longer exists
// (e.g. it was deleted) so deleted items don't linger on an item being edited.
// Non client/company concernings (static categories) are always kept.
export const filterActiveConcernings = (
    selectedConcernings: any[],
    clients: any[],
    companies: any[]
): any[] => {
    if (!selectedConcernings) {
        return [];
    }
    const clientIds = new Set((clients || []).map((c) => String(c.id)));
    const companyIds = new Set((companies || []).map((c) => String(c.id)));
    return selectedConcernings.filter((concerning) => {
        const ref = concerningRef(concerning);
        if (ref.startsWith('clients_')) {
            return clientIds.has(ref.replace('clients_', ''));
        }
        if (ref.startsWith('companies_')) {
            return companyIds.has(ref.replace('companies_', ''));
        }
        return true;
    });
}; 
// Resolves a concerning (string key, or {value,id} object) to the label shown
// on a chip. Client/company refs carry an id in the key, so their name has to
// be looked up in the loaded lists — a plain `concernings[key].label` lookup
// falls through and renders the raw `clients_<uuid>` instead.
//
// Shared so every dialog with a Concerning field renders chips identically.
// `translate` is the caller's i18next `t`.
export const getConcerningDisplayName = (
    concerning: any,
    clients: any[],
    companies: any[],
    translate: (key: string) => string
): string => {
    const ref = concerningRef(concerning);
    if (!ref) {
        return translate('Unknown concerning');
    }

    if (ref.startsWith('clients_')) {
        const clientId = ref.replace('clients_', '');
        const client = (clients || []).find((c) => String(c.id) === clientId);
        return client ? (client.name || client.description || `Client ${clientId}`) : ref;
    }

    if (ref.startsWith('companies_')) {
        const companyId = ref.replace('companies_', '');
        const company = (companies || []).find((c) => String(c.id) === companyId);
        return company ? (company.name || company.description || `Company ${companyId}`) : ref;
    }

    return translate(concernings[ref]?.label || ref);
};
